// src/utils/validation.js
// Production-Ready Input Validation & Formatting Utilities (Aadhaar Verhoeff, Indian Phone, PAN, IFSC, UPI)

/**
 * The Verhoeff algorithm multiplication table
 */
const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
]

/**
 * The Verhoeff permutation table
 */
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
]

/**
 * Validates a 12-digit Indian Aadhaar number using the Verhoeff checksum algorithm
 * @param {string} aadhaar
 * @returns {boolean}
 */
export function validateAadhaar(aadhaar) {
  if (!aadhaar) return false
  const clean = String(aadhaar).replace(/\D/g, '')
  if (clean.length !== 12) return false
  if (/^([0-1])\1{11}$/.test(clean)) return false // Reject repeating identical digits

  let c = 0
  const reversedArray = clean.split('').map(Number).reverse()

  for (let i = 0; i < reversedArray.length; i++) {
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][reversedArray[i]]]
  }

  return c === 0
}

/**
 * Formats a 12-digit Aadhaar input into standard `XXXX XXXX XXXX` chunks
 * @param {string} val
 * @returns {string}
 */
export function formatAadhaar(val) {
  if (!val) return ''
  const clean = String(val).replace(/\D/g, '').slice(0, 12)
  return clean.replace(/(\d{4})(?=\d)/g, '$1 ')
}

/**
 * Validates Indian 10-digit mobile number starting with 6, 7, 8, or 9
 * @param {string} phone
 * @returns {boolean}
 */
export function validatePhone(phone) {
  if (!phone) return false
  const clean = String(phone).replace(/\D/g, '')
  // Handle numbers with optional 91 or +91 prefix
  const tenDigit = clean.length === 12 && clean.startsWith('91') ? clean.slice(2) : clean
  return /^[6-9]\d{9}$/.test(tenDigit)
}

/**
 * Formats phone input to max 10 digits
 * @param {string} val
 * @returns {string}
 */
export function formatPhone(val) {
  if (!val) return ''
  const clean = String(val).replace(/\D/g, '')
  if (clean.length > 10 && clean.startsWith('91')) {
    return clean.slice(2, 12)
  }
  return clean.slice(0, 10)
}

/**
 * Validates Indian PAN card format (5 uppercase letters, 4 digits, 1 uppercase letter)
 * @param {string} pan
 * @returns {boolean}
 */
export function validatePan(pan) {
  if (!pan) return false
  const clean = String(pan).trim().toUpperCase()
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(clean)
}

/**
 * Validates Indian Bank IFSC Code (4 letters, 0, 6 alphanumeric characters)
 * @param {string} ifsc
 * @returns {boolean}
 */
export function validateIfsc(ifsc) {
  if (!ifsc) return false
  const clean = String(ifsc).trim().toUpperCase()
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(clean)
}

/**
 * Validates standard UPI Virtual Payment Address (VPA)
 * @param {string} upi
 * @returns {boolean}
 */
export function validateUpi(upi) {
  if (!upi) return false
  const clean = String(upi).trim()
  return /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(clean)
}
